import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { State } from "../../redux/reducers";
import { authSelector, userInfoSelector } from "../../redux/selectors/auth.selector";
import { G002ChatMessageCreateSchema, G002Schema } from "../../generated";
import { getG002, putChatMessage } from "../../services/api.service";
import { USER_TYPE_NUM_HEADQUARTER_USER, USER_TYPE_NUM_STAFF, initialChatDataModel, initialChatMessageModel } from "../../models/chat.model";
import "./G002.scss";
import { HEADQUARTER_USER, PARENT, RECORD_LIMIT, STAFF } from "../../constants";
import CustomTextarea from "../../components/CustomTextarea/CustomTextarea";
import ChatBubble from "../../components/ChatBubble/ChatBubble";
import LoadingSpinner from "../../components/Loading/LoadingSpinner";
import { useLoadingIndicator } from "../../hooks/LoadingIndicator.hooks";
import moment from "moment";
import { LuSendHorizonal } from "react-icons/lu";
import { MdOutlineEmojiEmotions } from "react-icons/md";
import { MdClose } from "react-icons/md";
import Picker, { EmojiStyle } from "emoji-picker-react";
import { useAuth } from "../../helper/auth.helper";
import { notifyError } from "../../helper/settings.helper";

interface iG002 {
  user_id: number;
  user_type: string;
  setRead: React.Dispatch<React.SetStateAction<boolean>>;
}

const G002: React.VFC<iG002> = ({ user_id, user_type, setRead }) => {
  const { checkLoggedIn } = useAuth();
  const authState = useSelector(authSelector);
  const userInfo = useSelector(userInfoSelector);
  const wsState = useSelector((s: State) => s.wsReducer);
  const [isLoading, , startLoading, stopLoading] = useLoadingIndicator();
  const [data, setData] = useState<G002Schema>(initialChatDataModel);
  const myUserType = userInfo?.user_type === HEADQUARTER_USER ? USER_TYPE_NUM_HEADQUARTER_USER : USER_TYPE_NUM_STAFF;
  const [textAreaHeight, setTextAreaHeight] = useState<number>();
  const [chatMessage, setChatMessage] = useState<G002ChatMessageCreateSchema>(initialChatMessageModel);
  const [sending, setSending] = useState<boolean>(false);
  const sendingRef = useRef<boolean>(false);
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const [showPicker, setShowPicker] = useState<boolean>(false);
  const pickerRef = useRef<HTMLDivElement | null>(null);
  const fetchChatMessagesRef = useRef<(silent?: boolean) => void>();
  const unreadPollingTimerRef = useRef<number | null>(null);
  const unreadPollingStopperRef = useRef<number | null>(null);
  const activePollingTimerRef = useRef<number | null>(null);
  const getRoomIds = useCallback(() => ({
    headquarter_id:
      user_type === HEADQUARTER_USER
        ? user_id
        : (userInfo?.user_type === HEADQUARTER_USER ? userInfo.id : undefined),
    staff_id: user_type === STAFF ? user_id : userInfo?.id,
    parent_id: user_type === PARENT ? user_id : undefined,
  }), [user_type, user_id, userInfo]);

  const setChatInfo = useCallback(() => {
    // 入力中のメッセージは保持しつつ、送信先情報のみ更新
    setChatMessage((prev) => ({
      ...prev,
      headquarter_staff_id: userInfo?.user_type === HEADQUARTER_USER ? userInfo.id : (user_type === HEADQUARTER_USER ? user_id : undefined),
      staff_id: user_type === STAFF ? user_id : userInfo?.id,
      parent_id: (user_type === PARENT ? user_id : undefined),
    }));
  }, [userInfo, user_id, user_type]);

  const onEmojiClick = (emojiObject: any, event: any) => {
    setChatMessage({ ...chatMessage, message: chatMessage.message + emojiObject.emoji });
    setShowPicker(false);
  };

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  const fetchChatMessages = useCallback(async (silent?: boolean) => {
    if (!silent) startLoading();
    try {
      const res = await getG002(user_type, user_id, undefined, authState);
      // APIは降順。UIは昇順に並べ替え
      const next = { ...res.data, messages: [...res.data.messages].reverse() } as G002Schema;

      // 変更検知（件数、末尾ID、is_readの変化）
      const prev = data;
      const prevLen = prev?.messages?.length || 0;
      const nextLen = next?.messages?.length || 0;
      const prevLastId = prevLen > 0 ? prev.messages[prevLen - 1].id : undefined;
      const nextLastId = nextLen > 0 ? next.messages[nextLen - 1].id : undefined;

      let changed = prevLen !== nextLen || prevLastId !== nextLastId;
      // 既読変化（特に相手からのメッセージが既読になった）を検出
      let madePartnerRead = false;
      const prevMap = new Map(prev.messages.map((m) => [m.id, m.is_read]));
      for (const m of next.messages) {
        const before = prevMap.get(m.id);
        if (before !== undefined) {
          if (before !== m.is_read) {
            changed = true;
            if (m.message_from !== myUserType && before === false && m.is_read === true) {
              madePartnerRead = true;
            }
          }
        } else {
          // 新規メッセージ（prevに存在しない）。相手からのメッセージが既読状態で入ってきたら通知対象
          if (m.message_from !== myUserType && m.is_read === true) {
            madePartnerRead = true;
          }
        }
      }

      if (changed) {
        setData(next);
        // 新規メッセージ到着時のみスクロール
        if (nextLen > prevLen || nextLastId !== prevLastId) {
          setTimeout(() => scrollToBottom(), 50);
        }
        // 親リスト更新の通知も、変更があったときのみ
        setRead((v) => !v);
        // 既読になったことを他クライアントへWS通知（初回ロードや既読変化時）
        const partnerHasMessages = next.messages.some((m) => m.message_from !== myUserType);
        if (partnerHasMessages && (madePartnerRead || prevLen === 0)) {
          wsState.socket?.emit("read_chat", getRoomIds());
        }
      }
    } catch (err) {
      checkLoggedIn(err);
      if (!silent) notifyError("データの取得に失敗しました。");
      console.error(err);
    } finally {
      if (!silent) stopLoading();
    }
  }, [user_id, user_type, authState, data, myUserType, getRoomIds, wsState.socket, checkLoggedIn, setRead, startLoading, stopLoading]);

  useEffect(() => {
    fetchChatMessagesRef.current = (silent?: boolean) => {
      fetchChatMessages(silent);
    };
  }, [fetchChatMessages]);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setShowPicker(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => { document.removeEventListener("mousedown", handleClick); };
  });

  // 初期化（送信先情報の設定と、初回読み込みのみローディング表示）
  useEffect(() => {
    setChatInfo();
    fetchChatMessages(false);
  }, [setChatInfo, fetchChatMessages]);

  // 接続開通や相手変更時にチャットルームへ参加
  useEffect(() => {
    if (!wsState.socket) return;
    wsState.socket.emit("create_chat_room", {
      headquarter_id: user_type === HEADQUARTER_USER ? user_id : (userInfo?.user_type === HEADQUARTER_USER ? userInfo.id : undefined),
      staff_id: user_type === STAFF ? user_id : userInfo?.id,
      parent_id: user_type === PARENT ? user_id : undefined,
    });
  }, [wsState.connected, user_id, user_type, userInfo, wsState.socket]);

  useEffect(() => {
    if (!wsState.socket) return;

    // この画面で開いている会話とWSイベントの対象が一致するか判定
    const isMyCurrentRoom = (payload: any) => {
      if (!payload) return false;
      const expected_headquarter_id = user_type === HEADQUARTER_USER
        ? user_id
        : (userInfo?.user_type === HEADQUARTER_USER ? userInfo.id : undefined);
      const expected_staff_id = user_type === STAFF
        ? user_id
        : (userInfo?.user_type === STAFF ? userInfo.id : undefined);
      const expected_parent_id = user_type === PARENT ? user_id : undefined;

      const { headquarter_id, staff_id, parent_id } = payload || {};

      // 指定されているIDだけ厳密一致（未定義は無視）
      if (expected_headquarter_id !== undefined && expected_headquarter_id !== headquarter_id) return false;
      if (expected_staff_id !== undefined && expected_staff_id !== staff_id) return false;
      if (expected_parent_id !== undefined && expected_parent_id !== parent_id) return false;
      return true;
    };

    const refetchWithDelayIfTarget = (payload?: any) => {
      if (!isMyCurrentRoom(payload)) return;
      // DB反映よりWS通知が先行する場合に備え、短い遅延で再取得をリトライ
      const delays = [200, 800];
      delays.forEach((d) => setTimeout(() => fetchChatMessagesRef.current?.(true), d));
    };

    // 新実装（roomベース）
    wsState.socket.on("receive_chat", refetchWithDelayIfTarget);
    // 既読などルーム単位の更新通知（旧実装互換・将来の拡張に備える）
    wsState.socket.on("update_chat_room", refetchWithDelayIfTarget);
    // 旧実装（login/partnerベース）
    wsState.socket.on("update_chat_message", refetchWithDelayIfTarget);

    return () => {
      wsState.socket?.off("receive_chat", refetchWithDelayIfTarget);
      wsState.socket?.off("update_chat_room", refetchWithDelayIfTarget);
      wsState.socket?.off("update_chat_message", refetchWithDelayIfTarget);
    };
  }, [wsState.socket, user_id, user_type, userInfo]);

  const clickRegister = async () => {
    if (chatMessage.message.trim() === "") return;
    if (sendingRef.current) return; // 二重送信ガード

    const messageToSend = chatMessage.message;
    startLoading();
    setSending(true);
    sendingRef.current = true;
    
    try {
      await putChatMessage(chatMessage, RECORD_LIMIT, authState);
      setChatMessage(prevMessage => ({ ...prevMessage, message: "" }));
      
      // WebSocketでリアルタイム通知を送信
      wsState.socket?.emit("send_chat", {
        headquarter_id: user_type === HEADQUARTER_USER ? user_id : (userInfo?.user_type === HEADQUARTER_USER ? userInfo.id : undefined),
        staff_id: user_type === STAFF ? user_id : userInfo?.id,
        parent_id: user_type === PARENT ? user_id : undefined,
        messages: messageToSend,
      });
      
      // メッセージ送信後にチャットを更新
      await fetchChatMessages(true);
    } catch (err) {
      checkLoggedIn(err);
      notifyError("登録に失敗しました。");
      console.error(err);
    } finally {
      stopLoading();
      setSending(false);
      sendingRef.current = false;
    }
  };

  useEffect(() => {
    let client_h: number = document.getElementById("message_send_area")?.clientHeight ?? 0;
    setTextAreaHeight(client_h);
  }, [chatMessage]);

  // 相手がメッセージを閲覧して既読になった場合にも画面が更新されるように
  // 自分が送った未読メッセージが存在する間だけ短周期で再取得するポーリングを実施
  //（サーバーから既読用のWSイベントが来ないケースのフォールバック）
  const startUnreadPolling = useCallback(() => {
    if (unreadPollingTimerRef.current != null) return;
    // 2秒間隔で再取得
    unreadPollingTimerRef.current = window.setInterval(() => {
      fetchChatMessagesRef.current?.(true);
    }, 2000) as unknown as number;

    // 念のため自動停止（60秒）
    if (unreadPollingStopperRef.current == null) {
      unreadPollingStopperRef.current = window.setTimeout(() => {
        if (unreadPollingTimerRef.current != null) {
          window.clearInterval(unreadPollingTimerRef.current);
          unreadPollingTimerRef.current = null;
        }
        if (unreadPollingStopperRef.current != null) {
          window.clearTimeout(unreadPollingStopperRef.current);
          unreadPollingStopperRef.current = null;
        }
      }, 60000) as unknown as number;
    }
  }, []);

  const stopUnreadPolling = useCallback(() => {
    if (unreadPollingTimerRef.current != null) {
      window.clearInterval(unreadPollingTimerRef.current);
      unreadPollingTimerRef.current = null;
    }
    if (unreadPollingStopperRef.current != null) {
      window.clearTimeout(unreadPollingStopperRef.current);
      unreadPollingStopperRef.current = null;
    }
  }, []);

  // 未読（自分が送信したメッセージで未読）が存在する間だけポーリングを回す
  useEffect(() => {
    const hasMyUnread = data?.messages?.some(
      (m) => m.message_from === myUserType && !m.is_read
    );
    if (hasMyUnread) {
      startUnreadPolling();
    } else {
      stopUnreadPolling();
    }
    return () => {
      // アンマウントや依存変更時に確実に停止
      stopUnreadPolling();
    };
    // data.messages と myUserType の変化を監視
  }, [data?.messages, myUserType, startUnreadPolling, stopUnreadPolling]);

  // 追加フォールバック: 画面が開いている間は低頻度ポーリングで新着を取り込む
  // - WSが届かない環境/タイミングでも数秒以内に更新されるようにする
  useEffect(() => {
    // 可視状態のみ更新（タブ非表示時は無駄な通信を避ける）
    const canPoll = () => document.visibilityState === "visible";

    const startActivePolling = () => {
      if (activePollingTimerRef.current != null) return;
      const intervalMs = wsState.connected ? 5000 : 2000; // WS未接続時はやや高頻度
      activePollingTimerRef.current = window.setInterval(() => {
        if (canPoll()) {
          fetchChatMessagesRef.current?.(true);
        }
      }, intervalMs) as unknown as number;
    };

    const stopActivePolling = () => {
      if (activePollingTimerRef.current != null) {
        window.clearInterval(activePollingTimerRef.current);
        activePollingTimerRef.current = null;
      }
    };

    // 起動時/接続状態変化時に再設定
    stopActivePolling();
    startActivePolling();

    const handleVisibility = () => {
      // タブ可視化時に即時反映、不可視時は停止
      if (document.visibilityState === "visible") {
        fetchChatMessagesRef.current?.();
        // タイマーが落ちていたら再開
        startActivePolling();
      } else {
        stopActivePolling();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      stopActivePolling();
    };
  }, [wsState.connected]);

  return (
    <>
      <LoadingSpinner isLoading={isLoading} />
      <div className={"chat_room_name"}>{data?.name}</div>
      <div ref={chatContainerRef} className="chat_log_items" style={{ height: `calc(100% - (100px + ${textAreaHeight}px))` }}>
        {data?.messages.map((message, i) => (
          <div key={i}>
            <ChatBubble
              isTalkMe={message.message_from === myUserType ? true : false}
              profile_pic_url={data.profile_pic_url}
              message={message.message}
              isRead={message.message_from === myUserType && message.is_read}
              sendDate={(i === 0 || moment(message.created_at).format("YYYY-MM-DD") !== (moment(data.messages[i - 1]?.created_at).format("YYYY-MM-DD"))) && moment(message.created_at).format("YYYY年MM月DD日")}
              sendTime={moment(message.created_at).format("HH:mm")}
            />
          </div>
        ))}
      </div>
      <div id="message_send_area" className="message_send_area">
        <div className="message_icon" onClick={() => showPicker ? setShowPicker(false) : setShowPicker(true)}>
          {showPicker ? <MdClose size="2rem" /> : <MdOutlineEmojiEmotions size="2rem" />}
        </div>
        <div className="message_input">
          <CustomTextarea
            placeholder="メッセージを入力"
            value={chatMessage.message}
            onChange={(v: any) => setChatMessage({ ...chatMessage, message: v })}
          />
        </div>
        <div
          className="message_icon"
          onClick={() => clickRegister()}
          style={{
            opacity: sending || chatMessage.message.trim() === "" ? 0.5 : 1,
            pointerEvents: sending || chatMessage.message.trim() === "" ? "none" : "auto",
          }}
          aria-disabled={sending || chatMessage.message.trim() === ""}
        >
          <LuSendHorizonal size="2rem" />
        </div>
      </div>
      {showPicker &&
        <div ref={pickerRef}>
          <Picker onEmojiClick={onEmojiClick} width="100%" emojiStyle={EmojiStyle.GOOGLE} />
        </div>
      }
    </>
  );
};

export default G002;
